// docFiling.js — the document FILING model (the file_exchange scope-update shape, the year list, the client-level
// sentinel). Imported by DocumentIntake (the triage screen), TaskEngine (filing a task attachment) and the scanner's
// file-without-scanning path. Keep this identical to intake's behavior — do not fork the filing model.
//
// ⛔ NO FOLDER OR TYPE NAMES LIVE HERE. The vocabulary is the doc_folders / doc_types tables (utils/docVocab, migration
// 2026-10-07_doc_folders_types). FOLDER_MAP, DOC_TYPE_GROUPS, FOLDER_LABEL and the derived folder lists were deleted
// when every pick list, the Folder Reference and the Documents screens moved onto the tables — do not reintroduce a
// code copy; add or rename a folder/type in Utilities → Table Management instead.


export const FILING_YEARS = Array.from({ length: 12 }, (_, i) => String(2018 + i));

// Sentinel for the filing forms' property picker: an EXPLICIT "file this at the client, no property".
// It is the authoritative signal — a client-level folder (doc_folders.level) only chooses the picker's DEFAULT, so a
// property-level type can still be filed client-level (a contract for one property is still a contract),
// and the choice is always visible in the control rather than implied by the doc type.
export const CLIENT_LEVEL = '__client_level__';

// ⛔ THE TWO FILING FORMS REACH CLIENT-LEVEL BY DIFFERENT MECHANISMS. This is a CHOSEN divergence
// (2026-08-13), not drift — do NOT "align" one to the other without reading this.
//   • TaskEngine — EXPLICIT: the property dropdown carries a "— File at Client Level (no property) —"
//     option (CLIENT_LEVEL). Works for ANY doc type, because "the type is property-level but THIS
//     instance isn't" is real (a contract covering one property is still a contract). Picking a
//     client-level type merely preselects that option; the visible selection wins.
//   • DocumentIntake — TYPE-DRIVEN: choosing a type in a client-level folder hides the property
//     picker and passes CLIENT_LEVEL. Only those types can go client-level there.
// WHY NOT THE SAME: Intake's property control is a search box + result list, not a dropdown, so the
// equivalent is pinning a "File at Client Level" row atop the results — a redesign of a working control
// on the screen Kassy uses daily, versus adding one option to an existing dropdown. Deferred on risk,
// not on principle. The intended end state IS the explicit option in both.
// WHEN CLOSING THE GAP: add the pinned row to Intake's property list; keep type-driven preselection as
// the default. Both paths must keep passing CLIENT_LEVEL — it, not the doc type, is what nulls the
// property, and it is what defeats the existingPropertyId fallback in buildFilingUpdates.

// The exact file_exchange scope-update intake writes on "File" (see DocumentIntake.fileDocument).
// Stamps client_id (the filing form always requires a client) so the filed doc reaches the per-client Files list,
// not just the property-keyed views. property_id falls back to the row's existing property_id when no new property
// is chosen (same as intake).
// A client-level result also carries clearParcelDocs: the caller must DELETE the file's parcel_documents row —
// nulling parcel_id here isn't enough, the join table is a second source of parcel visibility.
//
// ⛔ THE FOLDER COMES FROM THE TABLE (doc_types → doc_folders, utils/docVocab), never from the caller. `docType` may be a
// type key, its stored name or its display name. Keys AND the stored names are written: the keys are what screens
// will group on, the names keep every reader that still compares strings correct. An UNKNOWN type THROWS — it used
// to file silently into 'Other', a folder no screen lists, which is a document nobody finds again.
//
// ⛔ A CONSULTANT-LEVEL type (its folder's level is 'consultant') is filed against a consultant FIRM, not a client:
// `consultantId` and a year are required (it throws without them), and client, property and parcel are all cleared —
// the database refuses a consultant document that carries any of them, and a consultant folder without a consultant
// (migration 2026-10-08_consultant_doc_level). Staff only: both viewable flags are forced off whatever the caller
// passed. Every other level writes consultant_id null, so re-filing a consultant document elsewhere detaches it.
//
// Returns { updates, folder, folderName, docFields, clientLevel, clearParcelDocs }:
//   folder     — the folder's STORED name (what parcel_documents and the activity log carry)
//   folderName — its display name, for messages
//   docFields  — { file_type, type_key, folder_name, folder_key }, to spread into a parcel_documents write
export function buildFilingUpdates({ vocab, docType, clientId, consultantId, propertyId, parcelId, year, viewableClient, viewableConsultant, existingPropertyId, staffName }) {
  if (!vocab) throw new Error('Document folders/types are still loading — try again in a moment.');
  const type = vocab.resolveType(docType);
  if (!type) throw new Error(`"${docType}" is not a document type in the folder table.`);
  const folder = vocab.folder(type.folder_key);
  const docFields = { file_type: type.legacy_name, type_key: type.key, folder_name: folder.legacy_name, folder_key: folder.key };
  if (folder.level === 'consultant') {
    if (!consultantId) throw new Error(`"${type.name}" is filed against a consultant — pick the consultant.`);
    if (!year) throw new Error(`"${type.name}" needs the ${vocab.yearLabel(folder.key).toLowerCase()}.`);
    const updates = {
      ...docFields,
      year_subfolder: year,
      consultant_id: consultantId,
      client_id: null, property_id: null, parcel_id: null,
      filed_by: staffName,
      filed_at: new Date().toISOString(),
      need_review: false,
      viewable_client: false,
      viewable_consultant: false,
    };
    return { updates, folder: folder.legacy_name, folderName: folder.name, docFields, clientLevel: false, clearParcelDocs: true };
  }
  // The CLIENT_LEVEL sentinel — an explicit pick in the form's property control — is what decides scope, not
  // the doc type. It also defeats the existingPropertyId fallback below, so a stale prefill can't staple a
  // property onto a document the user deliberately filed at the client. (The folder's level picks the form's
  // default selection; see CLIENT_LEVEL. Neither says anything about the YEAR — 'Annual Review' is client-level
  // AND year-foldered.)
  const clientLevel = propertyId === CLIENT_LEVEL;
  const updates = {
    ...docFields,
    year_subfolder: year || null,
    client_id: clientId || null,
    consultant_id: null,
    property_id: clientLevel ? null : (propertyId || existingPropertyId || null),
    filed_by: staffName,
    filed_at: new Date().toISOString(),
    need_review: false,
    viewable_client: !!viewableClient,
    viewable_consultant: !!viewableConsultant,
  };
  // Asymmetric on purpose: on the property path an unpicked parcel LEAVES an existing parcel_id alone (today's
  // behavior — always writing null would silently unlink parcel-filed docs). Client-level clears it outright.
  if (clientLevel) updates.parcel_id = null;
  else if (parcelId) updates.parcel_id = parcelId;   // parcel optional — same as intake
  return { updates, folder: folder.legacy_name, folderName: folder.name, docFields, clientLevel, clearParcelDocs: clientLevel };
}
